import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "wouter";

import { Pagination } from "@nens/chakra/components";
import { AuthMode, BulkExportType, getBulkExports } from "@nens/ldo/api-v1";
import {
  Breadcrumbs,
  BulkExportTypeDescription,
  ModeToggler,
  bulkExportTypeDescription,
} from "@nens/ldo/components";
import { useLocalStorage } from "@nens/react/hooks";
import { useTableData } from "@nens/react/hooks";

import { ExportItem } from "../export-item/export-item";
import { getBulkExportListUrl } from "../routes";

interface BulkExportListProps {
  exportType: BulkExportType;
}

export function BulkExportList({ exportType }: BulkExportListProps) {
  const intl = useIntl();
  const setLocation = useLocation()[1];

  const [authMode, actualSetAuthMode] = useLocalStorage<AuthMode>("ldo_auth_mode", "private");
  const [search, setSearch] = useState<string>("");
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const listUrl = getBulkExportListUrl(exportType);

  const { page, limit, offset, pageSize, setPageSize, nextPage, previousPage, setPage } =
    useTableData({ initialPageSize: 15 });

  const params: Record<string, string | number> = {
    mode: authMode,
    order_by: "-id",
    offset,
    limit,
  };
  if (search !== "") {
    params.search = search;
  }
  if (authMode === "private" && showArchived) {
    params.status = "archived";
  }

  // Note that this filters on all other statuses if no status filter is given
  const bulkExports = useQuery(getBulkExports(exportType, params));

  const setAuthMode = (newMode: AuthMode) => {
    // If set to public, remove status filters

    if (newMode !== authMode) {
      actualSetAuthMode(newMode);
      setPage(1);
      setShowArchived(false);
    }
  };

  const numPages = bulkExports.isSuccess ? Math.ceil(bulkExports.data.total / pageSize) : 0;
  // We store it in a React state, so that we can continue showing the old number while loading
  // a new page; saves UI flickering.
  const [currentNumPages, setCurrentNumPages] = useState(numPages);
  useEffect(() => {
    if (numPages > 0 && numPages !== currentNumPages) {
      setCurrentNumPages(numPages);
    }
  }, [currentNumPages, numPages]);

  return (
    <Box w="100%">
      <Breadcrumbs
        crumbs={[
          { label: intl.formatMessage({ id: "pages.home", defaultMessage: "Home" }), href: "/" },
          {
            label: bulkExportTypeDescription(intl, exportType),
            href: listUrl,
          },
        ]}
      />
      <Container maxW={"7xl"} py={"24px"} justifyItems="center" w="100%">
        <Heading mb={4} size="lg">
          <BulkExportTypeDescription exportType={exportType} />
        </Heading>
        <Flex flexDirection="row" justifyContent="space-between" width="100%" gap={4}>
          <Flex>
            <ModeToggler
              authMode={authMode}
              setAuthMode={setAuthMode}
              flexDirection="row"
              gap={4}
            />
          </Flex>
          {authMode === "private" ? (
            <Flex>
              <Checkbox
                isChecked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              >
                <FormattedMessage
                  id="export.show_archived"
                  defaultMessage="Show archived exports"
                />
              </Checkbox>
            </Flex>
          ) : null}
          <Flex>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon />
              </InputLeftElement>
              <Input
                placeholder={intl.formatMessage({
                  id: "placeholder.search",
                  defaultMessage: "Search",
                })}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
          </Flex>
          <Spacer />
          <Flex>
            <Button onClick={() => setLocation(`${listUrl}/new`)}>
              + <FormattedMessage id="export.create_new" defaultMessage="Create New Export" />
            </Button>
          </Flex>
        </Flex>

        <Flex
          minHeight={"300px"}
          border="1px solid #e2e8f0"
          my="24px"
          flexDirection={"column"}
          rounded="md"
        >
          <Flex borderBottom={"1px solid #e2e8f0"} flexDirection="row" alignItems="center">
            <Text p="12px" fontWeight="normal">
              <FormattedMessage id="export.history.title" defaultMessage="Export History" />
            </Text>
            <Flex
              h="12px"
              justifyContent="center"
              alignItems="center"
              color="bij12_1.700"
              fontWeight="semibold"
              bgColor={"#CC614D20"}
              fontSize={"sm"}
              rounded="full"
              p="14px"
              ml="2px"
            >
              {bulkExports.data?.total || "0"}
            </Flex>
          </Flex>
          {bulkExports.data?.items.length === 0 ? (
            <Flex
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
              flexDirection="column"
            >
              <Button variant="outline" my="2" onClick={() => setLocation(`${listUrl}/new`)}>
                Click to create a new export
              </Button>
            </Flex>
          ) : null}
          <SimpleGrid columns={1} spacing={0} border="1px solid #ddd">
            {bulkExports.data?.items.map((item) => {
              return <ExportItem bulkExport={item} exportType={exportType} key={item.id} />;
            })}
          </SimpleGrid>
        </Flex>
        <Pagination
          toPage1={() => setPage(1)}
          toLastPage={() => setPage(numPages)}
          setItemsPerPage={setPageSize}
          itemsPerPageChoices={[15, 30, 60, 100]}
          toPrevious={previousPage}
          toNext={page < numPages ? nextPage : undefined}
          page={page}
          numPages={currentNumPages}
        />
      </Container>
    </Box>
  );
}
